import BaseModal from '@/components/BaseModal';
import BoldText from '@/components/BoldText';
import ButtonIconVertical from '@/components/ButtonIconVertical';
import InventoryQuantityIcon from '@/components/Icons/InventoryQuantity';
import ProductTagIcon from '@/components/Icons/ProductTag';
import SKUTag from '@/components/Icons/SKU';
import RegularText from '@/components/RegularText';
import { conditionsToShowException } from '@/constants';
import { AppliedItemTypes, ItemConditionEnum } from '@/constants/enum';
import { USER_GUIDE_LINK } from '@/constants/path';
import { checkIsEmptyRequiredDataToShowException } from '@/helpers';
import useUserPlan from '@/hooks/useUserPlan';
import checkoutRuleSlice, {
  ICheckoutRuleErrorField,
  isDefaultOpenModalAddConditionCheckoutSelector,
} from '@/redux/slice/checkoutRule.slice';
import ruleSlice, { IRuleErrorField, isDefaultOpenModalAddConditionSelector } from '@/redux/slice/rule.slice';
import { useAppDispatch } from '@/redux/store';
import { IConditionValue, IItemCondition, ILocationCondition } from '@/types/newSetup.type';
import { Button, Divider, Icon, InlineStack, Link, Tooltip } from '@shopify/polaris';
import { ExternalIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { BoxIcon, BoxMinusIcon, BoxPlusIcon, CollectionIcon, StoreIcon } from '../Icons';
import SelectLocationCondition from '../ModalLocationCondition/SelectLocationCondition';
interface IProps {
  isOpenModal: boolean;
  setIsOpenModal: (isOpenModal: boolean) => void;
  onAdd: (key: IItemCondition['type'] | ILocationCondition['type']) => (value: IConditionValue[] | null) => void;
  data: IItemCondition[];
  type: AppliedItemTypes;
  isInCheckout?: boolean;
  dataLocationConditions?: ILocationCondition[];
  onAddLocationCondition?: (
    key: IItemCondition['type'] | ILocationCondition['type'],
  ) => (value: IConditionValue[] | null) => void;
}
const ModalItemCondition = ({
  isOpenModal,
  setIsOpenModal,
  onAdd,
  data,
  type: defaultType,
  isInCheckout,
  dataLocationConditions,
  onAddLocationCondition,
}: IProps) => {
  const dispatch = useAppDispatch();
  const isDefaultOpenModalAddConditionNormal = useSelector(isDefaultOpenModalAddConditionSelector);
  const isDefaultOpenModalAddConditionInCheckout = useSelector(isDefaultOpenModalAddConditionCheckoutSelector);

  const isDefaultOpenModalAddCondition = useMemo(() => {
    return isInCheckout ? isDefaultOpenModalAddConditionInCheckout : isDefaultOpenModalAddConditionNormal;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(isDefaultOpenModalAddConditionInCheckout),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(isDefaultOpenModalAddConditionNormal),
    isInCheckout,
  ]);

  const currentSlice = useMemo(() => {
    return isInCheckout ? checkoutRuleSlice : ruleSlice;
  }, [isInCheckout]);

  const [type, setType] = useState<AppliedItemTypes>(AppliedItemTypes.All);
  const { isFreePlan, isPlanLimited } = useUserPlan();

  useEffect(() => {
    !isOpenModal && setType(defaultType);
  }, [isOpenModal, defaultType]);
  const isAllIncludeSelected = useMemo(() => {
    return [ItemConditionEnum.IncludeProduct, ItemConditionEnum.IncludeCollection, ItemConditionEnum.IncludeVendor].every(
      (type) => data.some((item) => item.type === type),
    );
  }, [data]);
  const isAllExcludeSelected = useMemo(() => {
    return [ItemConditionEnum.ExcludeProduct, ItemConditionEnum.ExcludeCollection, ItemConditionEnum.ExcludeVendor].every(
      (type) => data.some((item) => item.type === type),
    );
  }, [data]);

  const listAppliedItemTypes = useMemo(() => {
    return [
      {
        icon: <BoxPlusIcon />,
        title: 'Include',
        description: 'Only show for selected items',
        key: AppliedItemTypes.Include,
        disabled: isFreePlan || (type === AppliedItemTypes.Exclude && !!data?.length) || isAllIncludeSelected,
        tooltip: isFreePlan
          ? 'Upgrade to enable'
          : isAllIncludeSelected
          ? 'All ‘include’ conditions were already added'
          : "Cannot ‘include' while ‘exclude' items.",
      },
      {
        icon: <BoxMinusIcon />,
        title: 'Exclude',
        description: 'Not show for excluded items, still show for others',
        key: AppliedItemTypes.Exclude,
        disabled: isFreePlan || (type === AppliedItemTypes.Include && !!data?.length) || isAllExcludeSelected,
        tooltip: isFreePlan
          ? 'Upgrade to enable'
          : isAllExcludeSelected
          ? 'All ‘exclude’ conditions were already added'
          : "Cannot ‘exclude' while ‘include' items. ",
      },
    ];
  }, [isFreePlan, type, data?.length, isAllIncludeSelected, isAllExcludeSelected]);

  const checkTypeIsExisted = useCallback(
    (type: [ItemConditionEnum, ItemConditionEnum]) => {
      return data.some((item) => item.type === type[0] || item.type === type[1]);
    },
    [data],
  );

  const listExcludeActions = useMemo(() => {
    const disableProduct = checkTypeIsExisted([ItemConditionEnum.IncludeProduct, ItemConditionEnum.ExcludeProduct]);

    const disableCollection = checkTypeIsExisted([ItemConditionEnum.IncludeCollection, ItemConditionEnum.ExcludeCollection]);

    const disableVendor = checkTypeIsExisted([ItemConditionEnum.IncludeVendor, ItemConditionEnum.ExcludeVendor]);

    const disableProductTag = checkTypeIsExisted([ItemConditionEnum.IncludeProductTag, ItemConditionEnum.ExcludeProductTag]);

    const disableSKU = checkTypeIsExisted([ItemConditionEnum.IncludeSKU, ItemConditionEnum.ExcludeSKU]);

    // const disableInventoryLocation = checkTypeIsExisted([
    //   ItemConditionEnum.IncludeInventoryLocation,
    //   ItemConditionEnum.ExcludeInventoryLocation,
    // ]);

    // const disableProductMetafield = checkTypeIsExisted([
    //   ItemConditionEnum.IncludeProductMetafield,
    //   ItemConditionEnum.ExcludeProductMetafield,
    // ]);

    // const disableVariantMetafield = checkTypeIsExisted([
    //   ItemConditionEnum.IncludeVariantMetafield,
    //   ItemConditionEnum.ExcludeVariantMetafield,
    // ]);

    const tempItems: {
      icon: JSX.Element;
      description: string;
      key: ItemConditionEnum;
      disabled: boolean;
      tooltip: string;
      type: string;
      errorMsg: string;
      isHide?: boolean;
    }[] = [
      {
        icon: <BoxIcon />,
        description: 'Products, variants',
        key: type === AppliedItemTypes.Include ? ItemConditionEnum.IncludeProduct : ItemConditionEnum.ExcludeProduct,
        disabled: disableProduct || isFreePlan,
        tooltip: isFreePlan ? 'Upgrade to Standard to enable' : 'Condition was already added',
        type: 'product',
        errorMsg: 'Please select at least 1 product or variant',
      },
      {
        icon: <CollectionIcon />,
        description: 'Collections',
        key: type === AppliedItemTypes.Include ? ItemConditionEnum.IncludeCollection : ItemConditionEnum.ExcludeCollection,
        disabled: disableCollection || isFreePlan,
        tooltip: isFreePlan ? 'Upgrade to Standard to enable' : 'Condition was already added',
        type: 'collection',
        errorMsg: 'Please select at least 1 collection',
      },
      {
        icon: <StoreIcon />,
        description: 'Vendors',
        key: type === AppliedItemTypes.Include ? ItemConditionEnum.IncludeVendor : ItemConditionEnum.ExcludeVendor,
        disabled: disableVendor || isFreePlan,
        tooltip: isFreePlan ? 'Upgrade to Standard to enable' : 'Condition was already added',
        type: 'vendor',
        errorMsg: 'Please select at least 1 vendor',
      },
      {
        icon: <ProductTagIcon />,
        description: 'Product tags',
        key: type === AppliedItemTypes.Include ? ItemConditionEnum.IncludeProductTag : ItemConditionEnum.ExcludeProductTag,
        disabled: disableProductTag || isPlanLimited,
        tooltip: isPlanLimited ? 'Upgrade to Pro to enable' : 'Condition was already added',
        type: 'productTags',
        errorMsg: 'Please select at least 1 product tag',
      },
      {
        icon: <SKUTag />,
        description: 'SKUs',
        key: type === AppliedItemTypes.Include ? ItemConditionEnum.IncludeSKU : ItemConditionEnum.ExcludeSKU,
        disabled: disableSKU || isPlanLimited,
        tooltip: isPlanLimited ? 'Upgrade to Pro to enable' : 'Condition was already added',
        type: 'skus',
        errorMsg: 'Please select at least 1 SKU',
      },
      // {
      //   icon: <InventoryLocationIcon />,
      //   description: 'Inventory locations',
      //   key:
      //     type === AppliedItemTypes.Include
      //       ? ItemConditionEnum.IncludeInventoryLocation
      //       : ItemConditionEnum.ExcludeInventoryLocation,
      //   disabled: disableInventoryLocation || isPlanLimited,
      //   tooltip: isPlanLimited ? 'Upgrade to Pro to enable' : 'Condition was already added',
      //   type: 'inventoryLocations',
      //   errorMsg: 'Please select at least 1 inventory location',
      //   isHide: isInCheckout,
      // },
      // {
      //   icon: <ProductMetafieldIcon />,
      //   description: 'Product metafield',
      //   key:
      //     type === AppliedItemTypes.Include
      //       ? ItemConditionEnum.IncludeProductMetafield
      //       : ItemConditionEnum.ExcludeProductMetafield,
      //   disabled: disableProductMetafield || isPlanLimited,
      //   tooltip: isPlanLimited ? 'Upgrade to Pro to enable' : 'Condition was already added',
      //   type: 'productMetafield',
      //   errorMsg: 'Please select at least 1 metafield',
      //   isHide: isInCheckout,
      // },
      // {
      //   icon: <ProductMetafieldIcon />,
      //   description: 'Variant metafield',
      //   key:
      //     type === AppliedItemTypes.Include
      //       ? ItemConditionEnum.IncludeVariantMetafield
      //       : ItemConditionEnum.ExcludeVariantMetafield,
      //   disabled: disableVariantMetafield || isPlanLimited,
      //   tooltip: isPlanLimited ? 'Upgrade to Pro to enable' : 'Condition was already added',
      //   type: 'variantMetafield',
      //   errorMsg: 'Please select at least 1 metafield',
      //   isHide: isInCheckout,
      // },
    ];

    return tempItems.filter((i) => !i.isHide);
  }, [checkTypeIsExisted, type, isFreePlan, isPlanLimited]);

  const isInventoryQuantityAdded = data.some((item) => item.type === ItemConditionEnum.InventoryQuantity);

  const isDisableAddException = useMemo(() => {
    const isHasException = data.some((item) => item.type === ItemConditionEnum.Exception);
    const isHasConditionToEnableException = data.some((item) => conditionsToShowException.includes(item.type));

    const isEmptyRequiredData = checkIsEmptyRequiredDataToShowException(data);

    return {
      isHasException,
      isHasConditionToEnableException,
      isEmptyRequiredData,
    };
  }, [data]);

  return (
    <BaseModal
      title={isInCheckout ? 'Select a condition' : 'Select an item condition'}
      open={isOpenModal}
      onClose={() => {
        setIsOpenModal(false);
      }}
      size="large"
    >
      <div className="d-flex flex-column">
        <BoldText>Item condition</BoldText>
        <RegularText>Choose a trigger and item type.</RegularText>
        <InlineStack gap="200">
          {listAppliedItemTypes.map((item, index) => (
            <div key={index} style={{ flex: 1 }}>
              <ButtonIconVertical
                icon={item.icon}
                title={item.title}
                children={item.description}
                onClick={() => setType(item.key)}
                selected={type === item.key && !isAllExcludeSelected && !isAllIncludeSelected}
                disabled={item.disabled}
                tooltip={item.disabled && item.tooltip}
              />
            </div>
          ))}
        </InlineStack>
        {(!!type || isFreePlan) && (
          <div className="mt-16">
            <InlineStack gap="200" wrap>
              {listExcludeActions.map((item) => (
                <div key={item.key} style={{ flex: 1 }}>
                  <ButtonIconVertical
                    icon={item.icon}
                    children={item.description}
                    onClick={() => {
                      onAdd(item.key)([{ data: '' }]);
                      setIsOpenModal(false);
                      dispatch(
                        currentSlice.actions.handleSetIsDefaultOpenModalAddCondition({
                          ...isDefaultOpenModalAddCondition,
                          [item.type]: true,
                        }),
                      );
                      dispatch(
                        currentSlice.actions.handleSetError({
                          //@ts-ignore-next-line
                          value: { field: item.type as IRuleErrorField | ICheckoutRuleErrorField, msg: item.errorMsg },
                        }),
                      );
                    }}
                    disabled={item.disabled}
                    tooltip={item.disabled && item.tooltip}
                    fullHeight
                  />
                </div>
              ))}
            </InlineStack>
          </div>
        )}
      </div>

      {isInCheckout ? null : (
        <div className="d-flex flex-column mt-16">
          <BoldText>Other</BoldText>
          <div className="mt-8" style={{ width: '33.33%', maxWidth: '110px', minWidth: '90px' }}>
            <ButtonIconVertical
              icon={<InventoryQuantityIcon />}
              children={'Inventory quantity'}
              onClick={() => {
                onAdd(ItemConditionEnum.InventoryQuantity)([{ data: '' }]);
                setIsOpenModal(false);
              }}
              disabled={isInventoryQuantityAdded || isPlanLimited}
              tooltip={
                isInventoryQuantityAdded ? 'Inventory quantity is already added' : isPlanLimited ? 'Upgrade to Pro to enable' : ''
              }
            />
          </div>
        </div>
      )}

      <div className="d-flex flex-column mt-16">
        <BoldText>Exceptions</BoldText>
        <RegularText>
          Allow certain Products to be <b>unaffected</b> by rule. Cannot be used together with Product or SKU condition.
        </RegularText>
        <div className="mt-8">
          {!isDisableAddException.isHasConditionToEnableException ||
          isDisableAddException.isHasException ||
          isDisableAddException.isEmptyRequiredData ||
          isFreePlan ? (
            <Tooltip
              content={
                isFreePlan
                  ? 'Upgrade to enable'
                  : !isDisableAddException.isHasConditionToEnableException || isDisableAddException.isEmptyRequiredData
                  ? 'Cannot be used together with Product or SKU condition.'
                  : 'Condition was already added'
              }
            >
              <Button
                disabled={
                  !isDisableAddException.isHasConditionToEnableException ||
                  isDisableAddException.isHasException ||
                  isDisableAddException.isEmptyRequiredData ||
                  isFreePlan
                }
              >
                Add exceptions
              </Button>
            </Tooltip>
          ) : (
            <Button
              tone="critical"
              onClick={() => {
                onAdd(ItemConditionEnum.Exception)([{ data: '' }]);
                setIsOpenModal(false);
                dispatch(
                  currentSlice.actions.handleSetError({
                    value: {
                      //@ts-ignore-next-line
                      field: 'exceptionProduct' as IRuleErrorField | ICheckoutRuleErrorField,
                      msg: 'Please select at least 1 product or variant',
                    },
                  }),
                );
                dispatch(
                  currentSlice.actions.handleSetIsDefaultOpenModalAddCondition({
                    ...isDefaultOpenModalAddCondition,
                    exception: true,
                  }),
                );
              }}
            >
              Add exceptions
            </Button>
          )}
        </div>
      </div>

      {isInCheckout && onAddLocationCondition && (
        <div className="mt-16">
          <Divider />
          <div className="d-flex flex-column mt-8">
            <BoldText>Shipping address condition</BoldText>
            <RegularText>Based on shipping address provided by customers</RegularText>

            <div className="d-flex align-center gap-8 mt-16">
              <SelectLocationCondition
                data={dataLocationConditions || []}
                isInCheckout={isInCheckout}
                setIsOpenModal={setIsOpenModal}
                onAdd={onAddLocationCondition}
              />
            </div>
          </div>
        </div>
      )}

      <div className="mt-12">
        <Link
          url={isInCheckout ? USER_GUIDE_LINK.CHECKOUT_RULE_SETUP : USER_GUIDE_LINK.RULE_SETUP}
          target="_blank"
          removeUnderline
        >
          <div className="d-flex">
            {isInCheckout ? 'Learn more about ETA checkout conditions' : 'Learn more about display conditions'}
            <Icon source={ExternalIcon} />
          </div>
        </Link>
      </div>
    </BaseModal>
  );
};

export default ModalItemCondition;
